import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { devtools } from 'zustand/middleware'
import type { User, TeacherProfile } from '../types/user'
import { authApi, profileApi } from '../services/teacherApi'
import toast from 'react-hot-toast'
import i18n from '../i18n/config'

interface AuthState {
  user: User | null
  teacherProfile: TeacherProfile | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>
  loginWithPhone: (phone: string, password: string) => Promise<void>
  register: (userData: any) => Promise<void>
  logout: () => void
  updateProfile: (profileData: Partial<TeacherProfile>) => Promise<void>
  fetchProfile: () => Promise<void>
  setToken: (token: string) => void
  clearAuth: () => void
}

export const useAuthStore = create<AuthState & AuthActions>()(
  devtools(
    persist(
      (set, get) => ({
        // 初始状态
        user: null,
        teacherProfile: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,

        // 登录 (邮箱)
        login: async (email: string, password: string) => {
          set({ isLoading: true })
          try {
            const response = await authApi.login(email, password)

            if (response.success && response.data) {
              const { user, token, refreshToken } = response.data

              // 设置 token
              set({
                user,
                token,
                refreshToken: refreshToken || null,
                isAuthenticated: true,
                isLoading: false
              })

              const lang = (i18n.language || 'en').toLowerCase()
              const msg = lang.startsWith('zh') ? `登录成功！欢迎回来，${user.full_name}` : `Signed in! Welcome back, ${user.full_name}`
              toast.success(msg)

              // 获取教师资料
              await get().fetchProfile()
            } else {
              throw new Error(response.error?.message || '登录失败')
            }
          } catch (error: any) {
            set({ isLoading: false })
            toast.error(error.message || '邮箱或密码错误，请重试')
            throw error
          }
        },

        // 登录 (手机号)
        loginWithPhone: async (phone: string, password: string) => {
          set({ isLoading: true })
          try {
            const response = await authApi.loginWithPhone(phone, password)

            if (response.success && response.data) {
              const { user, token, refreshToken } = response.data

              set({
                user,
                token,
                refreshToken: refreshToken || null,
                isAuthenticated: true,
                isLoading: false
              })

              const lang2 = (i18n.language || 'en').toLowerCase()
              toast.success(lang2.startsWith('zh') ? '登录成功！' : 'Signed in!')

              // 获取教师资料
              await get().fetchProfile()
            } else {
              throw new Error(response.error?.message || '登录失败')
            }
          } catch (error: any) {
            set({ isLoading: false })
            toast.error(error.message || '登录失败')
            throw error
          }
        },

        // 注册
        register: async (userData: any) => {
          set({ isLoading: true })
          try {
            const response = await authApi.register(userData)

            if (response.success) {
              toast.success('注册成功！')
              set({ isLoading: false })
            } else {
              throw new Error(response.error?.message || '注册失败')
            }
          } catch (error: any) {
            set({ isLoading: false })
            toast.error(error.message || '注册失败')
            throw error
          }
        },

        // 登出
        logout: async () => {
          try {
            await authApi.logout()
          } catch (error) {
            console.error('登出API调用失败:', error)
          }

          set({
            user: null,
            teacherProfile: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false
          })
          const lang3 = (i18n.language || 'en').toLowerCase()
          toast.success(lang3.startsWith('zh') ? '已退出登录' : 'Signed out')
        },

        // 更新资料
        updateProfile: async (profileData: Partial<TeacherProfile>) => {
          try {
            const response = await profileApi.updateProfile(profileData)

            if (response.success && response.data) {
              set(state => ({
                teacherProfile: { ...state.teacherProfile, ...response.data }
              }))
              const lang4 = (i18n.language || 'en').toLowerCase()
              toast.success(lang4.startsWith('zh') ? '资料更新成功！' : 'Profile updated')
            } else {
              throw new Error(response.error?.message || '更新失败')
            }
          } catch (error: any) {
            toast.error(error.message || '更新失败')
            throw error
          }
        },

        // 获取资料
        fetchProfile: async () => {
          try {
            const response = await profileApi.getProfile()

            if (response.success && response.data) {
              const profileData = response.data
              // 后端返回的数据包含来自 users 表的合并字段
              const mergedData = profileData as any

              set((state) => ({
                teacherProfile: profileData,
                // 同步更新 user 对象的基础字段，确保界面能正确显示
                user: state.user ? {
                  ...state.user,
                  full_name: mergedData.full_name || state.user.full_name,
                  avatar_url: mergedData.avatar_url || state.user.avatar_url,
                  phone: mergedData.phone || state.user.phone,
                  country: mergedData.country || state.user.country,
                  timezone: mergedData.timezone || state.user.timezone,
                  residence_country: mergedData.residence_country || state.user.residence_country,
                } : state.user
              }))
            }
          } catch (error) {
            console.error('获取教师资料失败:', error)
          }
        },

        // 设置 Token
        setToken: (token: string) => {
          set({ token })
        },

        // 清除认证状态
        clearAuth: () => {
          set({
            user: null,
            teacherProfile: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false
          })
        }
      }),
      {
        name: 'talknect-teacher-auth',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          token: state.token,
          refreshToken: state.refreshToken,
          user: state.user,
          teacherProfile: state.teacherProfile,
          isAuthenticated: state.isAuthenticated
        }),
        onRehydrateStorage: () => (state) => {
          // 重新水化时不需要设置token，因为API请求时会自动从状态中获取
        }
      }
    ),
    { name: 'auth-store' }
  )
)
